import styled, { css } from 'styled-components';

import { OpenOutlineIc } from '@dsch/dd-icons';

import { BaseBadge } from '../BaseBadge/BaseBadge';
import { Tag } from '../Tags/Tag';
import { Link } from '../Button/Link';
import { LinkButton } from '../Button/LinkButton';

import { ThemeTypes } from '../../../types/Theme.namespace';
import { VARIANT, type Variant } from './Menu.typed';

type LinkStyledProps = {
  variant?: Variant;
  selected: boolean;
  color: ThemeTypes.Colors;
};

const Container = styled.ul`
  background-color: ${({ theme }) => theme.colors.WHITE};
  padding: 0;
  position: relative;
`;

type ItemStyledProps = {
  divider: boolean;
  selected: boolean;
};

export const Item = styled.li<ItemStyledProps>`
  flex: 1 1 auto;
  ${({ divider, theme }) =>
    divider &&
    css`
      &:before {
        display: block;
        height: 1px;
        margin-top: ${({ theme }) => theme.spacing.S4};
        margin-bottom: ${({ theme }) => theme.spacing.S4};
        background: ${theme.colors.GREY_LIGHT};
        content: '';
      }
    `}

  &:hover {
    ${({ selected, theme }) =>
      !selected && `background-color: ${theme.colors.OFFWHITE};`}
  }
`;

const ImageContainer = styled.div`
  margin-right: ${({ theme }) => theme.spacing.S8};
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
`;

const LinkCSS = css<LinkStyledProps>`
  ${({ theme }) => theme.fontSize.M16};
  color: ${({ color, theme }) => theme.colors[color]};
  padding: ${({ theme }) => `${theme.spacing.S12} ${theme.spacing.M16}`};
  display: flex;
  align-items: center;
  width: 100%;
  white-space: nowrap;
  height: 100%;
  font-weight: ${({ selected, theme }) =>
    selected ? theme.fontWeight.bold : theme.fontWeight.regular};

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
    border-radius: ${({ variant }) => variant === VARIANT.DASHBOARD && '2px'};
    color: ${({ color, theme }) => theme.colors[color]};
    text-decoration: none;
  }
`;

const StyledLink = styled(Link)<LinkStyledProps>`
  ${LinkCSS}
`;

const StyledLinkButton = styled(LinkButton)<LinkStyledProps>`
  ${LinkCSS}
`;

const NestedContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;

  > span {
    flex: 0 0 auto;
  }

  ${Item} {
    width: 100%;
  }

  ${Item}:first-child {
    ${StyledLink} {
      padding-right: 0;
    }
  }

  ${Item}:not(:first-child) {
    ${StyledLink} {
      padding-left: 6px;
    }
  }
`;

const NestedItem = styled.li`
  width: 100%;
`;

const StyledBadge = styled(BaseBadge)`
  margin: auto 0 auto ${({ theme }) => theme.spacing.S2};
  display: flex;
`;

const Slider = styled.span<{ index: number }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 48px;
  width: 100%;
  border: ${({ theme }) => `2px solid ${theme.colors.GREY_DARKER}`};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  pointer-events: none;

  transform: translateY(calc(${({ index }) => index * 48}px));
  transition: all 0.1s ease-in-out;
`;

const OpenIcon = styled(OpenOutlineIc)`
  margin-left: ${({ theme }) => theme.spacing.S8};
`;

const StyledTag = styled(Tag)`
  margin-left: ${({ theme }) => theme.spacing.S8};
`;

export {
  StyledTag,
  OpenIcon,
  Slider,
  StyledBadge,
  NestedItem,
  StyledLink,
  StyledLinkButton,
  NestedContainer,
  ImageContainer,
  Container,
};
